import { useTheme } from "styled-components";
import CaseNumberCell from "../Evidence/CaseNumberCell.js";
import ProgressSelector from "./ProgresSelector.js";
import StatusSelector from "./StatusSelector.js";
import AssignedUsersComponent from "./AssignUsersComponent.js";
import { getDateFormat } from "../../utils/date-format";
import PrioritySelector from "./PrioritySelector";
import CircularProgress from "./CircularProgress";
import * as Tooltip from "@radix-ui/react-tooltip";
import UserProgressInfo from "./UserProgressInfo";
import styled from "styled-components";
import TotalProgressInfo from "./TotalProgressInfo";

const StyledTooltipContent = styled(Tooltip.Content)`
  color: ${({ theme }) => theme.palette.text.primary};
  display: flex;
  flex-direction: column;
  pointer-events: none;
  justify-content: center;
  align-items: flex-start;
  z-index: 100000;
  background-color: ${({ theme }) => theme.palette.background.default};
  padding: 15px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  font-size: 0.75rem;
  max-width: 250px;
  width: 175px;

  h2,
  h3,
  h4 {
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

const CaseColumnDefs = [
  {
    dataField: "case_number",
    caption: "Case Number",
    render: (rowData) => {
      const CustomCell = ({ rowData }) => {
        const theme = useTheme();

        return (
          <CaseNumberCell
            rowData={rowData}
            color={theme.palette.primary.main}
          />
        );
      };

      return <CustomCell rowData={rowData} />;
    },
  },
  {
    dataField: "case_ref",
    caption: "Case Name",
  },
  {
    dataField: "progress",
    caption: "Progress",
    render: (rowData, options = {}) => {
      if (options?.ignoreRender === true) return rowData.progress;

      return (
        <ProgressSelector
          rowData={rowData}
          queryKey={options.queryKey}
          onChange={(data) => {
            options?.onRowUpdated?.(data);
          }}
        />
      );
    },
  },
  {
    dataField: "case_status",
    caption: "Status",
    render: (rowData, options = {}) => {
      if (options?.ignoreRender === true) return rowData.case_status;
      return (
        <StatusSelector
          rowData={rowData}
          queryKey={options.queryKey}
          onChange={(data) => {
            options?.onRowUpdated?.(data);
          }}
        />
      );
    },
  },
  {
    dataField: "user_progress",
    caption: "My Progress",
    render: (rowData, options = {}) => {
      if (options?.ignoreRender === true) return rowData.user_progress;
      if (
        !rowData.user_progress ||
        rowData.user_progress?.toLowerCase?.() === "not assigned" ||
        rowData.user_progress?.toLowerCase?.() === "nothing assigned"
      ) {
        return <CircularProgress percentage={rowData.user_progress} />;
      }
      return (
        <Tooltip.Provider delayDuration={600}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div>
                <CircularProgress
                  percentage={rowData?.user_progress?.progress_percentage}
                />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <StyledTooltipContent
                sideOffset={10}
                align="start"
                onClick={(e) => e.stopPropagation()}
                side="bottom"
              >
                <UserProgressInfo caseInfo={rowData} />
              </StyledTooltipContent>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    },
  },
  {
    dataField: "case_progress",
    caption: "Total Progress",
    render: (rowData, options = {}) => {
      if (options?.ignoreRender === true) return rowData.case_progress;
      if (
        !rowData.total_case_progress ||
        rowData.total_case_progress?.toLowerCase?.() === "not assigned" ||
        rowData.total_case_progress?.toLowerCase?.() === "nothing assigned"
      ) {
        return <CircularProgress percentage={rowData.total_case_progress} />;
      }
      return (
        <Tooltip.Provider delayDuration={600}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div>
                <CircularProgress
                  percentage={rowData.total_case_progress?.progress_percentage}
                />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <StyledTooltipContent
                sideOffset={10}
                align="start"
                onClick={(e) => e.stopPropagation()}
                side="bottom"
              >
                <TotalProgressInfo caseInfo={rowData} />
              </StyledTooltipContent>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    },
  },
  {
    dataField: "priority_name",
    caption: "Priority",
    render: (rowData, options = {}) => {
      if (options?.ignoreRender === true) return rowData.priority_name;
      return (
        <PrioritySelector
          rowData={rowData}
          onChange={(data) => {
            options?.onRowUpdated?.(data);
          }}
        />
      );
    },
  },
  {
    dataField: "assigned_users",
    caption: "Assigned Users",
    sorting: {
      enabled: false,
    },
    visible: true,
    render: (rowData) => {
      return <AssignedUsersComponent rowData={rowData} />;
    },
  },
  {
    dataField: "case_open_date",
    caption: "Case Open Date",
    dataType: "date",
    format: {
      type: getDateFormat({ isMoment: true, includeTime: false }),
    },
  },
  {
    dataField: "case_closed_date",
    caption: "Case Close Date",
    dataType: "date",
    format: {
      type: getDateFormat({ isMoment: true, includeTime: false }),
    },
  },
  {
    dataField: "last_activity_date",
    caption: "Last Activity Date",
    dataType: "date",
    format: {
      type: getDateFormat({ isMoment: true, includeTime: false }),
    },
  },
  {
    dataField: "case_lead",
    caption: "Case Lead",
    render: (rowData) => rowData?.case_lead?.full_name || null,
  },
  {
    dataField: "case_type",
    caption: "Type",
  },
  {
    dataField: "case_synopsis",
    caption: "Description",
    titleRender: (rowData) => rowData.case_synopsis,
    visible: false,
  },
  {
    dataField: "office_name",
    caption: "Office",
    visible: false,
  },
  {
    dataField: "client_name",
    caption: "Client Name",
    visible: false,
  },
  {
    dataField: "client_organization",
    caption: "Organization",
    visible: false,
  },
  {
    dataField: "evidence_count",
    caption: "Evidence Count",
    visible: false,
  },
  {
    dataField: "evidence_total_gb",
    caption: "Evidence Total (GB)",
    visible: false,
  },
  {
    dataField: "acquisition_count",
    caption: "Acquisition Count",
    visible: false,
  },
  {
    dataField: "acq_total_gb",
    caption: "Acquisition Total (GB)",
    visible: false,
  },
  {
    dataField: "active_task_count",
    caption: "Active Tasks",
    visible: false,
  },
];

export default CaseColumnDefs;
